import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../i18n/index";
import { TENANT_LOCALE_STORAGE_KEY } from "../i18n/config";
import { normalizeLocale } from "@/utils/locale";
import {
  getInterviewByToken,
  prepareForInterview,
} from "../services/interview.service";
import { InterviewResponse, InterviewStatus } from "../models/interview";
import { Loading } from "../components/Loading";
import { formatDateTime } from "../utils/date";
import { InterviewJobDetails } from "../components/interview/InterviewJobDetails";
import { InterviewOverviewPanel } from "../components/interview/InterviewOverviewPanel";
import { InterviewPreparationTips } from "../components/interview/InterviewPreparationTips";
import { InterviewPreparationConsent } from "../components/interview/InterviewPreparationConsent";
import { OverviewIconBadge } from "../components/interview/OverviewIconBadge";
import { CandidateFeedbackPrompt } from "../components/feedback/CandidateFeedbackPrompt";
import { Button } from "../components/ui/atoms/Button";
import { Card } from "../components/ui/atoms/Card";
import { Divider } from "../components/ui/atoms/Divider";
import { Typography } from "../components/ui/atoms/Typography";
import { createGTMTracking, getGTMAttributes } from "../utils/gtm/gtm-tracking";
import { AlertTriangle, Calendar, ClipboardCheck, Clock, Shield } from "lucide-react";

export const InterviewPreparationPage = () => {
  const { invitationToken } = useParams<{ invitationToken: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation('interview');
  const [interviewData, setInterviewData] = useState<InterviewResponse | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [interviewCompleted, setInterviewCompleted] = useState<boolean>(false);
  const [hasConsented, setHasConsented] = useState<boolean>(false);

  useEffect(() => {
    setInterviewCompleted(location.state?.interviewCompleted ?? false);
  }, [location.state?.interviewCompleted]);

  useEffect(() => {
    if (!invitationToken) {
      setError(i18n.t('errors.token_missing', { ns: 'interview' }));
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getInterviewByToken(invitationToken);
        if (cancelled) return;
        if (response.error) {
          setError(response.error);
        } else if (response.data) {
          setInterviewData(response.data);
        }
      } catch (err) {
        if (cancelled) return;
        setError(i18n.t('errors.fetch_failed', { ns: 'interview' }));
        console.error(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [invitationToken]);

  useEffect(() => {
    const rawLocale = interviewData?.locale;
    if (!rawLocale) return;
    void i18n.changeLanguage(normalizeLocale(rawLocale));
    return () => {
      const tenantLocale =
        localStorage.getItem(TENANT_LOCALE_STORAGE_KEY) || 'en';
      void i18n.changeLanguage(tenantLocale);
    };
  }, [interviewData?.locale]);


  const calculateRemainingDays = (expiryDateString: string) => {
    try {
      const expiryDate = new Date(expiryDateString);
      const today = new Date();
      const diffTime = expiryDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays > 0 ? diffDays : 0;
    } catch (error) {
      console.error("Error calculating remaining days:", error);
      return 0;
    }
  };

  const startInterviewProcess = async () => {
    if (!interviewData || !invitationToken) {
      setError(t('errors.start_missing_data'));
      return;
    }

    try {
      setIsLoading(true);

      const response = await prepareForInterview(invitationToken);

      if (response.error) {
        setError(response.error);
        setIsLoading(false);
        return;
      }

      if (!response.data) {
        setError(t('errors.start_no_data'));
        setIsLoading(false);
        return;
      }

      navigate(`/actual-interview/${response.data.interview_id}`, {
        state: {
          timeRemainingMinutes: response.data.time_remaining_minutes,
          startedAt: response.data.started_at,
          interviewStatus: response.data.status,
          talentName: interviewData.talent.name,
          jobTitle: interviewData.job_offer.title,
          invitationToken: invitationToken,
          workspaceLogoUrl: interviewData.workspace_logo_url ?? null,
          locale: interviewData.locale,
          needsMediaSetup: true,
        },
      });
    } catch (err) {
      setError(t('errors.start_error'));
      console.error(err);
      setIsLoading(false);
    }
  };

  const handleConsentChange = (consentGiven: boolean) => {
    setHasConsented(consentGiven);
  };

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex flex-col items-center justify-center p-4 font-body">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center animate-[fade-in-up_0.5s_ease-out]">
          <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5 border border-red-100">
            <AlertTriangle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-2xl font-black text-dark-invirtus mb-3 font-heading tracking-tight">{t('errors.access_error')}</h1>
          <p className="text-dark-invirtus/70 text-lg leading-relaxed">{error}</p>
        </div>
      </div>
    );
  }

  if (!interviewData) {
    return (
      <div className="min-h-screen bg-[#F8F8F8] flex flex-col items-center justify-center p-4 font-body">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center animate-[fade-in-up_0.5s_ease-out]">
          <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-5 border border-gray-200">
            <Shield className="w-8 h-8 text-gray-400" />
          </div>
          <h1 className="text-2xl font-black text-dark-invirtus mb-3 font-heading tracking-tight">
            {t('errors.session_not_found')}
          </h1>
          <p className="text-dark-invirtus/70 text-lg leading-relaxed">
            {t('errors.session_not_found_desc')}
          </p>
        </div>
      </div>
    );
  }

  const { interview, talent, job_offer } = interviewData;
  const brandLogoSrc = interviewData.workspace_logo_url || "/logo/dark-logo.png";
  const brandLogoAlt = job_offer.company_name || "Invirtus";
  const isInterviewFinished =
    interview.status === InterviewStatus.COMPLETED || interviewCompleted;
  const companyContactName =
    job_offer.company_name && job_offer.company_name.trim().length > 0
      ? job_offer.company_name.trim()
      : t('errors.hiring_team_fallback');
  const workspacePrimaryColor = interviewData.workspace_primary_color;
  const workspaceSecondaryColor = interviewData.workspace_secondary_color;
  const isValidHexColor = (value?: string | null): value is string =>
    Boolean(value && /^#[0-9A-Fa-f]{6}$/.test(value));
  const hasBrandedButtonColors =
    isValidHexColor(workspacePrimaryColor) &&
    isValidHexColor(workspaceSecondaryColor);
  const brandedButtonStyle = hasBrandedButtonColors
    ? {
        backgroundColor: workspacePrimaryColor,
        borderColor: workspacePrimaryColor,
        color: workspaceSecondaryColor,
      }
    : undefined;

  return (
    <div className="min-h-screen bg-[#F8F8F8] font-body pb-24 selection:bg-purple/30 selection:text-dark-invirtus">
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-up {
          opacity: 0;
          animation: fadeInUp 0.8s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }
        .delay-1 { animation-delay: 150ms; }
        .delay-2 { animation-delay: 300ms; }
        .delay-3 { animation-delay: 450ms; }
        .delay-4 { animation-delay: 600ms; }


      `}</style>

      {/* A. THE HERO BANNER */}
      <div className="w-full bg-gradient-1 bg-dotted relative overflow-hidden border-b border-gray-200/50 shadow-sm pt-12 pb-16 px-6 md:px-12">
        {/* Cinematic Ornaments */}
        <div style={{ animation: 'var(--animate-float-slow)' }} className="absolute top-0 left-10 md:left-24 w-64 h-64 bg-white/40 rounded-full blur-[80px]" />
        <div style={{ animation: 'var(--animate-float-medium)' }} className="absolute bottom-0 right-10 md:right-24 w-80 h-80 bg-purple/30 rounded-full blur-[120px]" />

        <div className="relative z-10 max-w-5xl mx-auto flex flex-col items-center text-center animate-fade-up">
          <div className="flex items-center gap-4 mb-4">
            <img
              src={brandLogoSrc}
              alt={`${brandLogoAlt} Logo`}
              className="h-16 w-16 md:h-[4.5rem] md:w-[4.5rem] object-contain"
            />
            <h1 className="text-4xl md:text-5xl font-black text-dark-invirtus tracking-tighter font-heading drop-shadow-sm leading-[1.1]">
              {t('welcome', { name: talent.name })}
            </h1>
          </div>

          <p className="text-lg md:text-xl text-dark-invirtus/80 font-body max-w-2xl font-medium leading-relaxed">
            {isInterviewFinished
              ? t('completion_message', { company: companyContactName })
              : t('deadline_message')}
          </p>
        </div>
      </div>

      {/* B. JOB & EXPIRY TELEMETRY */}
      <div className="max-w-6xl mx-auto -mt-10 relative z-20 px-4 sm:px-6 lg:px-8">
        <div className="bg-white/95 backdrop-blur-xl rounded-[1.25rem] shadow-[0_20px_40px_-15px_rgba(0,0,0,0.06)] border border-gray-100/80 p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-8 animate-fade-up delay-1">
          {/* Job Info */}
          <div className="flex-1 w-full text-center md:text-left">
            <h2 className="text-2xl md:text-3xl font-bold text-dark-invirtus font-heading tracking-tight mb-2">
              {t('interview_for')}{' '}
              <span
                className="mx-1.5 md:mx-2 text-dark-invirtus"
              >
                —
              </span>{' '}
              {job_offer.title}
              <span className="block text-lg text-dark-invirtus/50 font-medium mt-1">{t('at_company', { company: job_offer.company_name || t('errors.company_fallback') })}</span>
            </h2>
            {!isInterviewFinished && (
              <div className="flex items-center justify-center md:justify-start gap-2 mt-4 font-body text-dark-invirtus/70 text-sm font-semibold bg-gray-50 inline-flex px-3 py-1.5 rounded-md border border-gray-100">
                <Calendar
                  className="w-4 h-4 text-dark-invirtus"
                />
                <span>{t('available_until', { date: formatDateTime(interview.expires_at) })}</span>
              </div>
            )}
          </div>

          {/* Telemetry Metrics */}
          <div className={`flex items-center gap-6 md:gap-8 w-full md:w-auto overflow-x-auto ${isInterviewFinished ? '' : 'bg-gray-50/70 p-4 md:p-5 rounded-[1.25rem] border border-gray-100/50'}`}>
            {!isInterviewFinished && (
              <>
                <div className="flex flex-col items-center justify-center min-w-[5rem]">
                  <div className="text-4xl font-black text-dark-invirtus tracking-tighter font-heading leading-none mb-1">
                    {calculateRemainingDays(interview.expires_at)}
                  </div>
                  <div className="text-[0.65rem] uppercase tracking-[0.2em] text-dark-invirtus/50 font-bold whitespace-nowrap">
                    {t('days_left_label', { count: calculateRemainingDays(interview.expires_at) })}
                  </div>
                </div>
                <div className="hidden sm:block w-px h-16 bg-gradient-to-b from-transparent via-gray-300 to-transparent"></div>
              </>
            )}

            <div className="flex flex-col justify-center">
              <span
                className={`flex items-center justify-center px-4 md:px-5 py-2.5 rounded-full text-sm font-bold font-body uppercase tracking-wider relative overflow-hidden group ${interview.status === InterviewStatus.SCHEDULED
                  ? "bg-purple-soft text-dark-invirtus border border-purple/30"
                  : interview.status === InterviewStatus.IN_PROGRESS
                    ? "bg-blue-soft text-dark-invirtus border border-blue/30 shadow-[0_0_15px_rgba(192,225,255,0.5)]"
                    : interview.status === InterviewStatus.COMPLETED
                      ? "bg-[#E8F5E9] text-[#2E7D32] border border-[#A5D6A7]"
                      : "bg-red-soft text-dark-invirtus border border-red/30"
                  }`}
              >
                {/* Glowing dot for In Progress */}
                {interview.status === InterviewStatus.IN_PROGRESS && (
                  <span className="absolute left-3 w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
                )}
                <span className={interview.status === InterviewStatus.IN_PROGRESS ? "pl-5" : ""}>
                  {interview.status === InterviewStatus.SCHEDULED
                    ? t('status.scheduled')
                    : interview.status === InterviewStatus.IN_PROGRESS
                      ? t('status.in_progress')
                      : interview.status === InterviewStatus.COMPLETED
                        ? t('status.completed')
                        : t('status.cancelled')}
                </span>
              </span>
            </div>
          </div>
        </div>
        <div className="mt-12 mb-12">
          {/* Feedback block */}
          {invitationToken && (
            <div className="mb-10 animate-fade-up delay-2">
              <CandidateFeedbackPrompt
                invitationToken={invitationToken}
                interviewStatus={interview.status}
              />
            </div>
          )}

          {!isInterviewFinished && (
            <div className="flex flex-col w-full pb-12 gap-8">

              {/* IN PROGRESS OR INTERRUPTED RETURN BLOCK */}
              {interview.status === InterviewStatus.IN_PROGRESS && (
                <div className="p-8 md:p-12 bg-dark-invirtus rounded-2xl shadow-2xl relative overflow-hidden border border-dark-invirtus-strong animate-fade-up delay-2 group">
                  <div className="absolute inset-0 bg-gradient-3 opacity-10 group-hover:opacity-20 transition-opacity duration-700 pointer-events-none" />
                  <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
                    <div className="flex items-start gap-6">
                      <div className="p-5 bg-white/10 rounded-2xl backdrop-blur-md shadow-inner border border-white/5 flex-shrink-0">
                        <Clock className="w-10 h-10 text-white" />
                      </div>
                      <div>
                      <h3 className="text-3xl font-black text-white mb-3 font-heading tracking-tight">
                        {t('resume.title')}
                      </h3>
                      <p className="text-gray-300 font-body text-lg leading-relaxed max-w-lg">
                        {t('resume.description')}
                      </p>
                      </div>
                    </div>
                    <Button
                      onClick={startInterviewProcess}
                      label={t('resume.button')}
                      type="contained"
                      intent="primary"
                      size="lg"
                      className="w-full md:w-auto shadow-[0_0_30px_rgba(217,173,255,0.2)] hover:shadow-[0_0_40px_rgba(217,173,255,0.4)] transition-all"
                      id="return-to-interview-btn"
                    />
                  </div>
                </div>
              )}

                {/* 1. Job Details - Full Width */}
                <div className="animate-fade-up delay-2 w-full bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-8">
                  <InterviewJobDetails
                    jobOffer={job_offer}
                    workspacePrimaryColor={interviewData.workspace_primary_color}
                    workspaceSecondaryColor={interviewData.workspace_secondary_color}
                  />
                </div>

                {interviewData.overview ? (
                  <div className="animate-fade-up delay-3 w-full">
                    <InterviewOverviewPanel overview={interviewData.overview} origin="preparation_page" />
                  </div>
                ) : (
                  <div className="animate-fade-up delay-3 w-full">
                    <Card
                      variant="outlined"
                      padding="lg"
                      rounded="xl"
                      className="!m-0 w-full flex flex-col gap-4 !border-light-lila/60"
                    >
                      <Typography as="h3" variant="h2" color="primary">
                        {t('prep_tips.section_title')}
                      </Typography>
                      <InterviewPreparationTips />
                    </Card>
                  </div>
                )}

                {/* SECURITY & VIDEO PROTOCOLS — same Card/Step pattern as overview */}
                <div className="animate-fade-up delay-3 w-full">
                  <Card
                    variant="outlined"
                    padding="lg"
                    rounded="xl"
                    className="!m-0 w-full flex flex-col gap-5 md:gap-6 !border-light-lila/60"
                  >
                    <Typography as="h3" variant="h2" color="primary">
                      {t('security.title')}
                    </Typography>

                    <div className="flex flex-col gap-4">
                      <Card
                        variant="custom"
                        padding="md"
                        rounded="xl"
                        className="!m-0 border border-light-lila/60 bg-light"
                      >
                        <div className="flex items-start gap-3">
                          <OverviewIconBadge icon={ClipboardCheck} />
                          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                            <Typography as="h4" variant="h5" color="primary">
                              {t('security.criteria_title')}
                            </Typography>
                            <Typography
                              as="p"
                              variant="body-sm"
                              color="secondary"
                              className="leading-relaxed"
                            >
                              {t('security.criteria_desc')}
                            </Typography>
                          </div>
                        </div>
                      </Card>

                      <Card
                        variant="custom"
                        padding="md"
                        rounded="xl"
                        className="!m-0 border border-light-lila/60 bg-light"
                      >
                        <div className="flex items-start gap-3">
                          <OverviewIconBadge icon={AlertTriangle} />
                          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                            <Typography as="h4" variant="h5" color="primary">
                              {t('security.anti_cheat_title')}
                            </Typography>
                            <Typography
                              as="p"
                              variant="body-sm"
                              color="secondary"
                              className="leading-relaxed"
                            >
                              {t('security.anti_cheat_desc')}
                            </Typography>
                          </div>
                        </div>
                      </Card>
                    </div>

                    {interview.status === InterviewStatus.SCHEDULED && invitationToken && (
                      <>
                        <Divider />
                        <InterviewPreparationConsent
                          invitationToken={invitationToken}
                          onConsentChange={handleConsentChange}
                        />
                      </>
                    )}

                    {interview.status === InterviewStatus.SCHEDULED && (
                      <div className="w-full flex flex-col items-center pt-2">
                        <div className="w-full max-w-2xl text-center">
                          <Button
                            onClick={startInterviewProcess}
                            label={hasConsented ? t('start_button') : t('acknowledge_button')}
                            type="contained"
                            intent={hasConsented ? "secondary" : "muted"}
                            size="md"
                            disabled={!hasConsented}
                            className={`transition-all duration-300 ${hasConsented ? 'shadow-md hover:shadow-lg !border-0 ![box-shadow:none] ![--inv-stroke:transparent]' : ''}`}
                            style={hasConsented ? brandedButtonStyle : undefined}
                            id="start-interview-btn"
                            {...getGTMAttributes(
                              createGTMTracking('START_AI_TEST', {
                                origin: 'preparation_page',
                                status: interviewData.overview ? 'overview_shown' : 'overview_hidden',
                              })
                            )}
                          />
                          {!hasConsented && (
                            <Typography
                              as="p"
                              variant="body-xs"
                              color="secondary"
                              className="mt-4 font-bold uppercase tracking-widest"
                            >
                              {t('action_required')}
                            </Typography>
                          )}
                        </div>
                      </div>
                    )}
                  </Card>
                </div>
              </div>
          )}
        </div>
      </div>
    </div>
  );
};
